import { useCallback, useEffect, useState } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import { colors } from "../utils";

const Plot = createPlotlyComponent(Plotly);

const RadarPlot = ({
  isLoading,
  data,
  timebucketIdx,
  activeDomains,
  activeEntities,
  activeProxies,
}) => {
  const [chartData, setChartData] = useState(null);
  const [chartLayout, setChartLayout] = useState(null);
  const [showPlot, setShowPlot] = useState(false);

  const createChartLayout = () => {
    const plotLayout = {
      polar: {
        bgcolor: "rgba(0,0,0,0)",
        radialaxis: {
          visible: true,
          range: [-40, 100],
          tickvals: [0, 20, 40, 60, 80, 100],
          color: "white",
          width: 1,
          tickfont: { color: "white" },
          gridcolor: "gray",
          linecolor: "gray",
        },
        angularaxis: {
          color: "grey",
          gridcolor: "white",
          tickfont: {
            color: "white",
          },
        },
      },
      title: {
        text: "",
        font: {
          color: "white",
        },
      },
      width: "1200",
      height: "750",
      paper_bgcolor: "transparent", // "#111827",
      plot_bgcolor: "transparent", // "#111827",
      font: {
        color: "white",
        size: 16,
      },
      dragmode: false,
    };
    setChartLayout(plotLayout);
  };

  const createChartData = useCallback(() => {
    if (!data) return;
    const nodes = [...activeEntities, ...activeProxies];
    const plotData = nodes.map((node, idx) => {
      if (node.label === undefined || data[node.label] === undefined)
        return null;

      // Create interval notation for each entity datapoint
      const entityIntervals = activeDomains.map((domain) => {
        return `${
          data[node.label][domain?.value]?.value[
            node.label.includes(" Proxy") ? timebucketIdx : 0
          ]
        } [${
          data[node.label][domain?.value]?.value_lb[
            node.label.includes(" Proxy") ? timebucketIdx : 0
          ]
        }, ${
          data[node.label][domain?.value]?.value_ub[
            node.label.includes(" Proxy") ? timebucketIdx : 0
          ]
        }]`;
      });

      // Add the first datapoint again in order to prevent the radar chart from being open
      entityIntervals.push(
        `${
          data[node.label][activeDomains[0]?.value]?.value[
            node.label.includes(" Proxy") ? timebucketIdx : 0
          ]
        } [${
          data[node.label][activeDomains[0]?.value]?.value_lb[
            node.label.includes(" Proxy") ? timebucketIdx : 0
          ]
        }, ${
          data[node.label][activeDomains[0]?.value]?.value_ub[
            node.label.includes(" Proxy") ? timebucketIdx : 0
          ]
        }]`
      );

      // Generate chart data objects for value, upper bound and lower bound
      return [
        {
          type: "scatterpolar",
          r: entityIntervals.map((interval) =>
            parseInt(interval.split("[")[1].split(", ")[0])
          ),
          theta:
            activeDomains?.length > 0
              ? [
                  ...activeDomains.map((domain) => domain.label),
                  activeDomains[0].label,
                ]
              : [],
          mode: "lines",
          line: { color: "transparent" },
          name: "Lower Bound",
          showlegend: false,
          hoverinfo: "none",
        },
        {
          type: "scatterpolar",
          r: entityIntervals.map((interval) =>
            parseInt(interval.split(" [")[1].split(", ")[1].slice(0, -1))
          ),
          theta:
            activeDomains?.length > 0
              ? [
                  ...activeDomains.map((domain) => domain.label),
                  activeDomains[0].label,
                ]
              : [],
          mode: "lines",
          fill: "tonext", // Fills trace to previous trace
          fillcolor: node.label.includes(" Proxy")
            ? "rgba(255,255,255,0.4)"
            : colors[idx].replace("rgb", "rgba").replace(")", ",0.4)"),
          line: { color: "transparent" },
          name: "Upper Bound",
          showlegend: false,
          hoverinfo: "none",
        },
        {
          type: "scatterpolar",
          r: entityIntervals.map((interval) =>
            parseInt(interval.split(" [")[0].split(",")[0])
          ),
          theta:
            activeDomains?.length > 0
              ? [
                  ...activeDomains.map((domain) => domain.label),
                  activeDomains[0].label,
                ]
              : [],
          name: node.label,
          line: {
            color: node.label.includes(" Proxy")
              ? "rgba(255,255,255,1)"
              : colors[idx],
          },
          customdata: entityIntervals,
          hovertemplate:
            "<b>%{data.name}</b><br>%{theta}: %{customdata}<extra></extra>",
        },
      ];
    });
    setChartData(plotData.flat());
  }, [activeDomains, activeEntities, activeProxies, data, timebucketIdx]);

  useEffect(() => {
    if (
      isLoading ||
      !data ||
      (activeProxies.length === 0 && activeEntities.length === 0) ||
      activeDomains.length < 3
    ) {
      setShowPlot(false);
      setChartLayout(null);
      setChartData(null);
      return;
    }

    setShowPlot(true);
    createChartData();
    createChartLayout();
  }, [
    activeDomains,
    activeEntities,
    activeProxies,
    data,
    createChartData,
    isLoading,
  ]);

  return (
    <div className="flex flex-col items-center content-center min-w-50">
      {isLoading && <p className="text-center">Loading data...</p>}
      {!isLoading && chartLayout && chartData && showPlot && (
        <Plot data={chartData} layout={chartLayout} />
      )}
    </div>
  );
};

export default RadarPlot;
